import ThinkChinaLogomarkWhite from "@assets/images/tc-logomark-white@2x.svg";
import { ThemeEnum } from "@components/ThemeProvider/ThemeProvider";
import { faBars, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import useIsSmallScreen from "@hooks/useIsSmallScreen";
import { useScrollable } from "@hooks/useScrollable";
import { useCustomParams } from "@src/app/hooks/useCustomParams";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import { useHeaderSticky } from "@src/app/hooks/useHeaderSticky";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import Link from "../Link";
import Mask from "../Mask";
import {
  categoryDropdownItems,
  categoryNavLinks,
  isCategoryNavItemActive,
} from "./categoryData";
import CategoryDropdown from "./CategoryDropdown";
import CategoryNavItem from "./CategoryNavItem";
import IconButton from "./IconButton";
import Logo from "./Logo";
import MainNav from "./MainNav";
import MobileMenu from "./MobileMenu";
import ScrollIndicator from "./ScrollIndicator";

type HeaderProps = {
  theme?: ThemeEnum;
  className?: string;
};

export default function Header({
  theme = ThemeEnum.light,
  className,
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { isSticky, sentinelRef } = useHeaderSticky();
  const categoryContainerRef = useRef<HTMLDivElement>(null);

  const isDefault = theme === ThemeEnum.light;
  const isVideo = theme === ThemeEnum.dark;
  const { level1, level2 } = useCustomParams();
  const sendDataToGTM = useGTMDispatch();
  const location = useLocation();
  const isSmallScreen = useIsSmallScreen();
  const isCategoryScrollable = useScrollable(categoryContainerRef);

  const minimized = isSticky;

  useEffect(() => {
    setIsSearchOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  const getCategoryStyles = () => {
    const textColor = isDefault ? "text-primary" : "text-primary-on-dark";
    const bgColor = isDefault
      ? "bg-background-default"
      : "bg-background-primary-on-dark";
    const categoryBgColor = isDefault
      ? "bg-background-secondary"
      : "bg-background-secondary-on-dark";
    return { textColor, bgColor, categoryBgColor };
  };

  const { textColor, bgColor, categoryBgColor } = getCategoryStyles();

  return (
    <>
      <header
        data-testid="header"
        className={clsx(
          "flex w-full flex-col transition-all duration-300",
          className,
        )}
      >
        <div
          className={clsx(
            "container mx-auto flex w-full max-w-300 items-center justify-between overflow-hidden px-3 py-2 transition-all duration-300",
            "lg:px-3 lg:py-4",
            minimized ? "h-0 opacity-0" : "h-14 opacity-100 lg:h-auto",
            bgColor,
          )}
        >
          <Logo theme={theme} />
          <MainNav theme={theme} />
          <div className="flex items-center gap-3 lg:hidden">
            <IconButton
              icon={faMagnifyingGlass}
              onClick={() => {
                setIsSearchOpen(true);
              }}
              ariaLabel="Search"
              theme={theme}
            />
            <IconButton
              icon={faBars}
              onClick={() => {
                setIsMobileMenuOpen(true);
              }}
              ariaLabel="Menu"
              theme={theme}
            />
          </div>
        </div>
      </header>

      <div ref={sentinelRef} className="pointer-events-none h-px w-full" />
      <div
        style={{ position: "sticky", top: 0, zIndex: 50 }}
        className={clsx(
          "min-h-10 w-full px-3 py-1.5 transition-all duration-300 lg:py-0",
          categoryBgColor,
          "border-divider-brand border-b-2",
        )}
      >
        <div className="relative container mx-auto flex max-w-300 items-center justify-between">
          {minimized && (
            <Link to="/" className="flex shrink-0 items-center px-3 py-0">
              <img
                src={ThinkChinaLogomarkWhite}
                alt="ThinkChina"
                className="h-10 w-6.25 object-cover"
              />
            </Link>
          )}
          <div
            ref={categoryContainerRef}
            className="no-scrollbar flex flex-1 items-center overflow-x-auto lg:overflow-visible"
          >
            <div className="flex w-max items-center">
              {categoryNavLinks.map((category) => {
                const isActive = isCategoryNavItemActive(
                  location.pathname,
                  level1,
                  level2,
                  category,
                );

                return (
                  <div key={category.label} className="group relative">
                    <Link
                      to={`${category.href}?ref=header-page`}
                      className="flex shrink-0"
                      onClick={() => {
                        sendDataToGTM({
                          event: "tcCustomEvent",
                          eventCategory: "navigation",
                          eventAction: "click",
                          eventLabel: category.label.toLowerCase(),
                        });
                      }}
                    >
                      <CategoryNavItem
                        label={category.label}
                        hasDropdown={category.hasDropdown}
                        isActive={isActive}
                        isDropdownOpen={false}
                        textColor={textColor}
                        isDark={isVideo}
                        onToggle={() => {}}
                      />
                    </Link>

                    <CategoryDropdown
                      isDark={isVideo}
                      items={categoryDropdownItems[category.label]}
                      currentPath={location.pathname}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {isSmallScreen && isCategoryScrollable && (
            <ScrollIndicator
              containerRef={categoryContainerRef}
              theme={theme}
            />
          )}

          <div className="hidden shrink-0 items-center justify-end px-3 py-0 lg:flex">
            <IconButton
              icon={faMagnifyingGlass}
              onClick={() => {
                setIsSearchOpen(true);
              }}
              ariaLabel="Search"
              theme={theme}
            />
          </div>
        </div>
      </div>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => {
          setIsMobileMenuOpen(false);
        }}
        theme={theme}
      />

      <Mask
        isOpen={isSearchOpen}
        onClose={() => {
          setIsSearchOpen(false);
        }}
        searchValue={searchValue}
        onSearchChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setSearchValue(e.target.value);
        }}
        onSearchSubmit={(val: string) => {
          const q = val.trim();
          if (!q) return;
          window.location.assign(`/search?q=${encodeURIComponent(q)}`);
        }}
      />
    </>
  );
}
